import {observer} from "mobx-react-lite";
import AddonsStoreList, {AddonStore} from "../../stores/addons-store-list";
import SubscriptionPlansStore from "../../../../subscription-plans/stores/subscription-plans-store";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import {FONT_COLOR, FONT_VARIANT} from "../../../../../components/fonts/font-props";
import './summary-popup-modal.scss';
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import FontBody4 from "../../../../../components/fonts/font-body4/font-body4";
import {DefaultModalLayout} from "../../../../../components/modal/layouts/default-modal-layout";
import {Button} from "../../../../../components/inputs/button/button";
import {BUTTON_VARIANTS} from "../../../../../components/inputs/button-variants";
import React from "react";
import UnchangedAddonGridLine from "./components/unchanged-addons-grid-line/unchanged-addons-grid-line";
import PlanTotalPricingGridLine from "./components/plan-total-pricing/plan-total-pricing-grid-line";
import PlanPricingGridLine from "./components/plan-pricing/plan-pricing-grid-line";
import ChangedAddonGridLine from "./components/changed-addons-grid-line/changed-addons-grid-line";
import DateTimeV2, {DATETIME_VIEW} from "../../../../../components/datetime-v2/datetime-v2";
import {useDateTimeFormattingParams} from "../../../../../hooks/use-datetime-formatting-params";

interface IAddonSummaryPopup {
    cancelButton?: {
        title: string,
        onClick: () => void
    },
}

/**
 * Addons Summary popup modal.
 * Displays current vs. new billing breakdown with addon changes and total pricing.
 * Dates use DateTimeV2 to respect merchant timezone and format settings.
 */
export const AddonsSummaryPopup: React.FC<IAddonSummaryPopup> = observer((props) => {

    const planStore = SubscriptionPlansStore.userPlan;
    const basePrice = planStore?.formattedBasePrice ?? 0;
    const planPriceWithAddons = planStore?.priceWithAddons ?? 0;
    const userAddons = planStore?.userAddons;
    const userAddonsStores = AddonsStoreList.userAddonsStores;
    const ActiveUnchangedAddons = AddonsStoreList.getActiveUnchangedAddons();
    const dateTimeParams = useDateTimeFormattingParams();

    let priceToAdd = 0;
    AddonsStoreList.getAddonsToAdd().map(function (addon: AddonStore) {
        return priceToAdd += addon.addonPrice;
    });

    let priceToRemove = 0;
    AddonsStoreList.getAddonsToRemove().map(function (addon: AddonStore) {
        if (addon.isQuotaBased()) {
            return priceToRemove += -addon.addonPrice;
        }
        return priceToRemove += addon.addonPrice;
    });

    const updatedAt = SubscriptionPlansStore.userPlanUpdatedAt ?? '';
    const approveBilling = () => {
        AddonsStoreList.generateBillingLink().then(() => {
            cancel();
        });
    }

    const isApproveDisabled = (): boolean => {
        return AddonsStoreList.isGeneratingBillingLink || AddonsStoreList.isLoading;
    }

    const cancel = () => {
        props.cancelButton?.onClick();
    }

    return (
        <DefaultModalLayout
            isCloseDisabled={false}
            onCloseClicked={() => props.cancelButton?.onClick()}
        >
            <div className="summary-popup-modal">
                <div className="addons-summary-heading-row">
                    <FontBody1 color={FONT_COLOR.NEUTRAL3} variant={FONT_VARIANT.DEFAULT}>
                        Add-ons Summary
                    </FontBody1>
                </div>
                <div className="billing-carts-wrapper">
                    <div className="current-billing-wrapper">
                        <div className="current-billing-heading">
                            <div>
                                <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                    Current Billing
                                </FontBody1>
                            </div>
                            {
                                updatedAt &&
                                <div>
                                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>
                                        Updated on <DateTimeV2 {...dateTimeParams} utcDateString={updatedAt} dateTimeView={DATETIME_VIEW.DATE_ONLY} />, by Owner
                                    </FontBody2>
                                </div>
                            }
                        </div>
                        <div className="current-billing-plan-pricing">
                            <PlanPricingGridLine price={basePrice}/>
                        </div>
                        <div
                            className={`current-billing-addons-heading ${(userAddons && userAddons.length !== 0) && 'border-bottom-dashed'} `}>
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                Add-ons:
                            </FontBody2>
                        </div>
                        <div className="current-billing-addons-list">
                            {
                                (!userAddons || userAddons.length === 0) &&
                                <div className="no-addons-text-wrapper">
                                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>No add-ons currently active</FontBody2>
                                </div>
                            }
                            {userAddonsStores && userAddonsStores.map(function (addon: AddonStore) {
                                return <UnchangedAddonGridLine key={addon.id} addon={addon}/>
                            })}
                        </div>
                        <div className="current-billing-plan-total-pricing">
                            <PlanTotalPricingGridLine price={planPriceWithAddons.toFixed(2)}/>
                        </div>
                    </div>
                    <div className="new-billing-wrapper">
                        <div className="new-billing-heading">
                            <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                New Billing
                            </FontBody1>
                        </div>
                        <div className="new-billing-plan-pricing-wrapper">
                            <PlanPricingGridLine price={basePrice}/>
                        </div>
                        <div className="new-billing-addons-heading">
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                Add-ons:
                            </FontBody2>
                        </div>
                        <div className="addons-summary-list-wrapper">
                            <div className="addons-unchanged-list">
                                {
                                    ActiveUnchangedAddons.length > 0 &&
                                    <div className="addons-unchanged-list-heading">
                                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                            UNCHANGED
                                        </FontBody4>
                                    </div>
                                }
                                {
                                    ActiveUnchangedAddons.map(function (addon: AddonStore) {
                                        return <UnchangedAddonGridLine key={addon.id} addon={addon}/>
                                    })
                                }
                            </div>
                            <div className="addons-updated-list">
                                {
                                    AddonsStoreList.getAddonsToAdd().length > 0 &&
                                    <div className="addons-updated-list-heading">
                                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                            UPDATED
                                        </FontBody4>
                                    </div>
                                }
                                {
                                    AddonsStoreList.getAddonsToAdd().map(function (addon: AddonStore) {
                                        return <ChangedAddonGridLine key={addon.id} addon={addon} />
                                    })
                                }
                            </div>
                            <div className="addons-to-be-removed-list">
                                {
                                    AddonsStoreList.getAddonsToRemove().length > 0 &&
                                    <div className="addons-to-be-removed-heading">
                                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                            TO BE REMOVED
                                        </FontBody4>
                                    </div>
                                }
                                {
                                    AddonsStoreList.getAddonsToRemove().map(function (addon: AddonStore) {
                                        return <ChangedAddonGridLine key={addon.id} addon={addon} />
                                    })
                                }
                            </div>
                        </div>
                        <div className="new-billing-plan-total-pricing">
                            <PlanTotalPricingGridLine price={(planPriceWithAddons + priceToAdd - priceToRemove).toFixed(2)}/>
                        </div>
                    </div>
                </div>
                <div className="summary-popup-buttons-wrapper">
                    <Button variant={BUTTON_VARIANTS.Tertiary} onClick={cancel} text="Cancel"/>
                    <Button onClick={approveBilling}
                            disabled={isApproveDisabled()}
                            text={'Approve billing'} variant={BUTTON_VARIANTS.PrimaryAccent}
                    />
                </div>
            </div>
        </DefaultModalLayout>
    );
});
